/* eslint-disable camelcase */
import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import * as API from "services/api"
import { useCustomName } from "hooks"
import { DEPARTMENT_KEY } from "utils/customNameHelpers"
import StrikeAround from "shared/StrikeAround"
import {
  formSubmissionSlug as getFormSubmissionSlug,
  submissionPermissions as getSubmissionPermissions,
} from "reduxSlices/formSubmissionSlice"
import ContentHeader from "shared/ContentHeader"
import LoadingIndicator from "shared/LoadingIndicator"
import { errorToast } from "shared/toast"
import GroupAndUserAndDepartmentSearch from "./GroupAndUserAndDepartmentSearch"
import GroupGranteeList from "./GroupGranteeList"
import UserGranteeList from "./UserGranteeList"
import DepartmentGranteeList from "./DepartmentGranteeList"

export const ACCESS_TAB_ID = "tab-submission-access"
export const ACCESS_TAB_PANEL_ID = "tabpanel-submission-access"

const SubmissionAccess = () => {
  const [loading, setLoading] = useState(false)
  const [groupGranteeList, setGroupGranteeList] = useState([])
  const [userGranteeList, setUserGranteeList] = useState([])
  const [departmentGranteeList, setDepartmentGranteeList] = useState([])

  const formSubmissionSlug = useSelector(getFormSubmissionSlug)

  const { pluralName: departmentsName } = useCustomName(DEPARTMENT_KEY)

  const updateAccessGranteeLists = (data) => {
    setGroupGranteeList(data.groups)
    setUserGranteeList(data.users)
    setDepartmentGranteeList(data.departments)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let mounted = true

    /* eslint-disable consistent-return */
    const fetchAccessGrantees = async () => {
      if (!formSubmissionSlug) return null

      const response = await API.getFormSubmissionAccessGrantList({ formSubmissionSlug })

      if (!mounted) return

      if (response.ok) {
        updateAccessGranteeLists(response.data)
      } else {
        errorToast("Something went wrong.  Unable to load access data.")
      }

      setLoading(false)
    }
    /* eslint-enable consistent-return */

    setLoading(true)
    fetchAccessGrantees()

    return () => { mounted = false }
  }, [formSubmissionSlug])

  const createAccessLevelGrant = async (value) => {
    const [grantee_type, grantee_id] = value.split("-")
    const access_level_grant = { grantee_id, grantee_type }

    const response = await API.createFormSubmissionAccessGrant({
      formSubmissionSlug, access_level_grant,
    })
    if (response.ok) {
      updateAccessGranteeLists(response.data)
    } else {
      console.error("Error creating access level grant", response)
    }
  }

  const deleteFormSubmissionGrant = (grantId) => async () => {
    const response = await API.deleteFormSubmissionAccessGrant({ formSubmissionSlug, grantId })
    if (response.ok) {
      updateAccessGranteeLists(response.data)
    } else {
      console.error("Error deleting submission grant: ", response);
    }
  }

  const { canManageSubmissionGrants } = useSelector(getSubmissionPermissions)

  if (!formSubmissionSlug) return null

  return (
    <div id={ACCESS_TAB_PANEL_ID} className="relative grow" role="tabpanel" aria-labelledby={ACCESS_TAB_ID}>
      <div className="inner-container mx-auto max-w-screen-lg">
        <ContentHeader
          heading={
            <h2 className="header-title">Submission Access</h2>
          }
        />
        {
          loading
            ? <LoadingIndicator textValue="Loading submission access data ..." />
            : (
              <>
                {
                  canManageSubmissionGrants && (
                    <GroupAndUserAndDepartmentSearch
                      className="mb-8"
                      selectedGroups={groupGranteeList}
                      selectedUsers={userGranteeList}
                      selectedDepartments={departmentGranteeList}
                      onSearchSelected={createAccessLevelGrant}
                    />
                  )
                }
                <StrikeAround className="mb-2">
                  <h3>Groups</h3>
                </StrikeAround>
                <GroupGranteeList
                  className="mb-8"
                  formSubmissionSlug={formSubmissionSlug}
                  groups={groupGranteeList}
                  deleteFormSubmissionGrant={deleteFormSubmissionGrant}
                  updateAccessGranteeLists={updateAccessGranteeLists}
                  isDisabled={!canManageSubmissionGrants}
                />
                <StrikeAround className="mb-2">
                  <h3>Individuals</h3>
                </StrikeAround>
                <UserGranteeList
                  className="mb-8"
                  formSubmissionSlug={formSubmissionSlug}
                  users={userGranteeList}
                  deleteFormSubmissionGrant={deleteFormSubmissionGrant}
                  updateAccessGranteeLists={updateAccessGranteeLists}
                  isDisabled={!canManageSubmissionGrants}
                />
                <StrikeAround className="mb-2">
                  <h3>{departmentsName}</h3>
                </StrikeAround>
                <DepartmentGranteeList
                  formSubmissionSlug={formSubmissionSlug}
                  departments={departmentGranteeList}
                  deleteFormSubmissionGrant={deleteFormSubmissionGrant}
                  updateAccessGranteeLists={updateAccessGranteeLists}
                  isDisabled={!canManageSubmissionGrants}
                />
              </>
            )
        }
      </div>
    </div>
  )
}

export default SubmissionAccess
